import { lazy, Suspense } from 'react'

import { createFileRoute, retainSearchParams } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { graylogQueries } from '@/features/graylog/queries'

import { getCurrentTenant } from '@/shared/auth/store'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'

import { ErrorBoundary } from '@/components/common/error-boundary'

const EventDefinitionsPage = lazy(
	() => import('@/features/graylog/event-definitions/components/page'),
)

const searchSchema = z.object({
	q: z.string().optional(),
	action: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/graylog/event-definitions/')({
	validateSearch: zodValidator(searchSchema),

	search: {
		middlewares: [retainSearchParams(['q', 'action'])],
	},

	loader: async ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant() as string

		if (tenant) {
			await queryClient.ensureQueryData({
				...graylogQueries.events(tenant),
				revalidateIfStale: true,
			})
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Event Definitions | Graylog | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RoutePending,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	return (
		<div className="flex w-full flex-col items-start justify-start gap-6">
			<ErrorBoundary feature="event-definitions">
				<Suspense
					fallback={
						<TableFullSkeleton
							className="w-full"
							rows={10}
							withLastUpdated
							withFilters
						/>
					}
				>
					<EventDefinitionsPage />
				</Suspense>
			</ErrorBoundary>
		</div>
	)
}

function RoutePending() {
	return (
		<div className="flex w-full flex-col items-start justify-start gap-6">
			<TableFullSkeleton
				className="w-full"
				rows={10}
				withLastUpdated
				withFilters
			/>
		</div>
	)
}
